/**
 * Recherche de ville des rayons /recherche — logique UNIQUE partagée par la
 * barre de recherche de l'en-tête et la fenêtre « Filtres » (2026-09-26).
 *
 * Avant, la fenêtre des filtres ne savait filtrer que la liste des villes
 * déjà présentes sur Donoor, sans autocomplétion ni « ma position » : deux
 * comportements pour la même question (« où ? »). Désormais :
 *  · champ vide → villes où Donoor a des résultats (`cities`) ;
 *  · saisie (nom ou code postal) → toutes les communes françaises
 *    (geo.api.gouv.fr), anti-rafale 200 ms, réponse périmée ignorée ;
 *  · « Utiliser ma position actuelle » → géolocalisation du navigateur puis
 *    commune la plus proche.
 */
import { cityLookupApi, type CitySuggestion } from "~/services/cityLookup";

export interface VilleOption {
  name: string;
  slug: string;
  count?: number;
}

export interface VilleSuggestion {
  name: string;
  slug: string;
  postalCode?: string;
  /** Nombre de résultats Donoor (villes proposées quand le champ est vide). */
  count?: number;
  source: "donoor" | "geo";
}

export function useRechercheVille(cities: () => ReadonlyArray<VilleOption> | undefined) {
  const { t } = useI18n();
  const requete = ref("");
  const suggestionsGeo = ref<CitySuggestion[]>([]);
  const enCours = ref(false);
  let minuteur: ReturnType<typeof setTimeout> | null = null;

  watch(requete, (q) => {
    if (minuteur) clearTimeout(minuteur);
    const saisie = q.trim();
    if (!saisie) {
      suggestionsGeo.value = [];
      enCours.value = false;
      return;
    }
    enCours.value = true;
    minuteur = setTimeout(async () => {
      const resultats = await cityLookupApi.search(saisie);
      // L'utilisateur a continué de taper : cette réponse est périmée.
      if (q !== requete.value) return;
      suggestionsGeo.value = resultats;
      enCours.value = false;
    }, 200);
  });

  onBeforeUnmount(() => {
    if (minuteur) clearTimeout(minuteur);
  });

  const suggestions = computed<VilleSuggestion[]>(() => {
    if (requete.value.trim()) {
      return suggestionsGeo.value.map((c) => ({
        name: c.name,
        slug: c.slug,
        postalCode: c.postalCode,
        source: "geo" as const,
      }));
    }
    return (cities() ?? []).slice(0, 8).map((c) => ({
      name: c.name,
      slug: c.slug,
      count: c.count,
      source: "donoor" as const,
    }));
  });

  const geoEnCours = ref(false);
  const geoErreur = ref<string | null>(null);

  /** Commune de la position actuelle ; `null` (message dans `geoErreur`) sinon. */
  function localiser(): Promise<{ name: string; slug: string } | null> {
    return new Promise((resolve) => {
      if (!import.meta.client || !navigator.geolocation) {
        geoErreur.value = t("association.recherche.geoNonDisponible");
        resolve(null);
        return;
      }
      geoEnCours.value = true;
      geoErreur.value = null;
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const ville = await cityLookupApi.reverseGeocode(
            pos.coords.latitude,
            pos.coords.longitude,
          );
          geoEnCours.value = false;
          if (!ville) {
            geoErreur.value = t("association.recherche.villeIndeterminee");
            resolve(null);
            return;
          }
          resolve({ name: ville.name, slug: ville.slug });
        },
        (err) => {
          geoEnCours.value = false;
          geoErreur.value =
            err.code === err.PERMISSION_DENIED
              ? t("association.recherche.geoRefusee")
              : t("association.recherche.geoIndisponible");
          resolve(null);
        },
        { timeout: 8000, maximumAge: 60_000 },
      );
    });
  }

  return { requete, suggestions, enCours, geoEnCours, geoErreur, localiser };
}
